// src/CqcWorklist.js
// =============================================================================
// The Worklist — a flat, full-screen view of every checkable evidence
// element across every site, so nobody has to drill KEY QUESTION → QUALITY
// STATEMENT → SITE → EVIDENCE CATEGORY (CqcCompliance.js) just to find out
// what's still outstanding. One row per element; sites with a statement
// marked not-applicable (see cqcTaxonomy.js / cqc_store.js's applicability
// mechanism) simply don't generate a row for it — there's no work to track.
//
// Each row shows, compactly: status badge (+ a status-tinted row background
// and left border), site, the statement it belongs to, a proof-item count
// badge, who it's assigned to, and an estimated completion date. Everything
// else — the full Key Question › Quality statement › Evidence category
// path, the statement's purpose, what counts as proof, the actual list of
// proof items (many per element — link or upload, each viewable in a popup
// window), the contributor's own comment, and the message thread — is
// behind an expand/collapse icon per row, exactly so this view stays usable
// for "just pull the outstanding work together" rather than turning back
// into a drill-down.
//
// UPDATE 7: status is now MANUALLY set via a dropdown in the detail panel
// (assignment.status — see cqcRollup.js's elementStatus()), not derived from
// whether a source is on file. Proof is now a many-per-element list backed
// by the cqc_evidence_items collection (cqcApi.listEvidenceItems /
// addEvidenceItem / removeEvidenceItem) rather than the single
// cqc_attestations.documentRef pointer — that older single-source model is
// still what CqcCompliance.js's drill-down and CqcEvidenceDialog.js use, so
// the two views can show different things for the same element; see
// SETUP_CQC.md UPDATE 7 for why they weren't unified in this pass.
//
// UPDATE 8: the detail panel gained a second row of three bordered columns
// below the first — Messages (unchanged), a new append-only Contributor
// comment log (timestamped, immutable, newest entry on top — replaces the
// old single free-text field in this view specifically), and a new
// read-only Resources panel (per evidence category, from
// cqcTaxonomy.js's EVIDENCE_CATEGORIES[].resources). The status dropdown's
// "ongoing" value is now labelled "In progress" in the UI (the stored value
// is unchanged, so this is cosmetic only).
// =============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { GROUPS, elementGuidance } from "./cqcTaxonomy";
import { cqcApi } from "./cqcApi";
import { StatusBadge, statusRowStyle } from "./cqcColors";
import {
  applicabilityIndex, itemApplicableForSite,
  assignmentIndex, elementStatus, evidenceItemIndex,
} from "./cqcRollup";
import { MAX_UPLOAD_BYTES, fmtBytes, readFileAsBase64 } from "./CqcEvidenceDialog";
import CqcContributors from "./CqcContributors";
import CqcQuickAddContributor from "./CqcQuickAddContributor";

// Sentinel value for the assignee <select>'s "add a new one" option — kept
// out of the way of any real contributorId (a 24-hex Mongo id) so it can
// never collide.
const ADD_CONTRIBUTOR_SENTINEL = "__add_contributor__";

const STATUS_OPTIONS = [
  { value: "not-started", label: "Not started" },
  { value: "ongoing", label: "In progress" },
  { value: "completed", label: "Completed" },
];

const S = {
  wrap: { fontFamily: "system-ui, sans-serif", display: "flex", flexDirection: "column",
          height: "calc(100vh - 130px)", minHeight: 480 },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between",
            paddingBottom: 10, borderBottom: "1px solid #e5e7eb", flexShrink: 0 },
  h1: { fontSize: 20, fontWeight: 700, margin: 0 },
  sub: { fontSize: 12, color: "#6b7280" },
  manageBtn: { padding: "8px 14px", fontSize: 12.5, fontWeight: 600, border: "1px solid #d1d5db",
               borderRadius: 6, background: "#fff", cursor: "pointer" },
  filters: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "10px 0",
             borderBottom: "1px solid #f1f5f9", flexShrink: 0 },
  chip: (active) => ({ padding: "5px 11px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
                        border: active ? "1px solid #1e293b" : "1px solid #e5e7eb",
                        background: active ? "#1e293b" : "#fff", color: active ? "#fff" : "#374151" }),
  select: { padding: "6px 9px", fontSize: 12.5, border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit" },
  search: { padding: "6px 10px", fontSize: 12.5, border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit", width: 200 },
  checkLbl: { display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#374151", cursor: "pointer" },
  counts: { fontSize: 11.5, color: "#9ca3af", marginLeft: "auto" },
  listWrap: { flex: 1, overflow: "auto", minHeight: 0 },
  colHead: { display: "grid", gridTemplateColumns: "108px 150px 1fr 90px 170px 130px 34px", gap: 10,
             padding: "8px 10px", fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase",
             letterSpacing: 0.3, borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, background: "#fff", zIndex: 1 },
  row: (status) => ({ display: "grid", gridTemplateColumns: "108px 150px 1fr 90px 170px 130px 34px", gap: 10,
         alignItems: "center", padding: "9px 10px 9px 8px", borderBottom: "1px solid #f1f5f9", fontSize: 12.5,
         ...statusRowStyle(status) }),
  siteCell: { fontWeight: 600, color: "#374151" },
  labelBtn: { background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" },
  itemLabel: { fontWeight: 600, color: "#1e293b" },
  elLabel: { color: "#6b7280" },
  breadcrumb: { fontSize: 11, color: "#94a3b8", marginTop: 2 },
  proofBadge: { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 22, height: 22,
                padding: "0 6px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                background: "#eef2ff", color: "#3730a3", border: "1px solid #c7d2fe" },
  proofBadgeZero: { background: "#f8fafc", color: "#94a3b8", border: "1px solid #e2e8f0" },
  dateInput: { padding: "5px 7px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 5, fontFamily: "inherit", width: "100%" },
  expandBtn: { fontSize: 16, lineHeight: 1, width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center",
               color: "#374151", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer" },

  // Detail panel — "firm bold borders" per the redesign request: a 2px dark
  // outer border around the whole panel, and each of the 3 columns plus the
  // messages block below gets its own 2px bordered box, rather than the
  // single subtle #f8fafc panel this replaces.
  details: { gridColumn: "1 / -1", background: "#fff", border: "2px solid #1e293b", borderRadius: 10,
             padding: 14, marginTop: 4, marginBottom: 8, display: "flex", flexDirection: "column", gap: 12 },
  statusRow: { display: "flex", alignItems: "center", gap: 10, paddingBottom: 10, borderBottom: "2px solid #e2e8f0" },
  statusLabel: { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 },
  statusSelect: { padding: "6px 10px", fontSize: 12.5, fontWeight: 600, border: "2px solid #1e293b", borderRadius: 6, fontFamily: "inherit" },
  cols: { display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: 12, alignItems: "stretch" },
  col: { border: "2px solid #1e293b", borderRadius: 8, padding: 11, display: "flex", flexDirection: "column",
         minHeight: 0, background: "#fdfdff" },
  colLabel: { fontSize: 11, fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 6 },
  colBody: { fontSize: 12.5, color: "#374151", flex: 1 },
  bullets: { margin: "4px 0 0", paddingLeft: 18, color: "#475569" },
  textarea: { width: "100%", minHeight: 54, padding: "7px 9px", fontSize: 12.5, border: "1px solid #d1d5db",
              borderRadius: 6, fontFamily: "inherit", resize: "vertical" },

  // Proof-items column: a scrollable vertical list + an inline add-form.
  proofList: { flex: 1, overflow: "auto", minHeight: 60, maxHeight: 220, display: "flex", flexDirection: "column", gap: 7, marginBottom: 8 },
  proofItem: { display: "flex", alignItems: "flex-start", gap: 7, padding: "7px 8px", borderRadius: 7,
               border: "1px solid #e2e8f0", background: "#fff" },
  proofIcon: { fontSize: 14, flexShrink: 0, marginTop: 1 },
  proofMain: { flex: 1, minWidth: 0 },
  proofLabelBtn: { background: "none", border: "none", padding: 0, margin: 0, font: "inherit", fontWeight: 600,
                   color: "#2563eb", cursor: "pointer", textAlign: "left", wordBreak: "break-word" },
  proofMeta: { fontSize: 10.5, color: "#94a3b8", marginTop: 2 },
  proofReviewOk: { fontSize: 10.5, color: "#6b7280", marginTop: 2 },
  proofReviewOverdue: { fontSize: 10.5, color: "#991b1b", fontWeight: 700, marginTop: 2 },
  proofRemoveBtn: { fontSize: 14, lineHeight: 1, background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: "2px 3px" },
  proofEmpty: { fontSize: 12, color: "#9ca3af", padding: "10px 4px", textAlign: "center" },
  addForm: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 9, borderTop: "2px solid #e2e8f0" },
  addFormRow: { display: "flex", gap: 6, alignItems: "center" },
  kindToggle: (active) => ({ padding: "4px 9px", fontSize: 11, fontWeight: 600, borderRadius: 999, cursor: "pointer",
                              border: active ? "1px solid #1e293b" : "1px solid #d1d5db",
                              background: active ? "#1e293b" : "#fff", color: active ? "#fff" : "#374151" }),
  addInput: { flex: 1, padding: "6px 8px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit", minWidth: 0 },
  addBtn: { padding: "6px 12px", fontSize: 12, fontWeight: 600, border: "none", borderRadius: 6,
            background: "#1e293b", color: "#fff", cursor: "pointer", whiteSpace: "nowrap" },
  addHint: { fontSize: 10.5, color: "#94a3b8" },
  addErr: { fontSize: 11, color: "#991b1b" },

  msgList: { display: "flex", flexDirection: "column", gap: 8, marginBottom: 8, maxHeight: 180, overflow: "auto" },
  msg: (role) => ({ padding: "7px 10px", borderRadius: 8, fontSize: 12,
                     background: role === "supervisor" ? "#eff6ff" : "#fff",
                     border: `1px solid ${role === "supervisor" ? "#bfdbfe" : "#e5e7eb"}` }),
  msgMeta: { fontSize: 10.5, color: "#94a3b8", marginBottom: 3 },
  msgForm: { display: "flex", gap: 8, alignItems: "flex-start" },
  msgInput: { flex: 1, padding: "7px 9px", fontSize: 12.5, border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit" },
  sendBtn: { padding: "7px 12px", fontSize: 12, fontWeight: 600, border: "none", borderRadius: 6,
             background: "#1e293b", color: "#fff", cursor: "pointer" },
  mailLink: { fontSize: 11.5, color: "#2563eb" },

  // Contributor comment log (UPDATE 8) — append-only, newest entry on top,
  // no edit control anywhere: once posted a comment cannot be changed, only
  // added to. Deliberately its own visual rhythm (a form ABOVE the list,
  // since the newest post appears at the top right below it) rather than
  // Messages' form-below-list layout.
  commentForm: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 },
  commentList: { display: "flex", flexDirection: "column", gap: 7, maxHeight: 180, overflow: "auto" },
  commentItem: { padding: "7px 9px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff" },
  commentMeta: { fontSize: 10.5, color: "#94a3b8", marginBottom: 3, fontWeight: 700 },
  commentBody: { fontSize: 12.5, color: "#1e293b", whiteSpace: "pre-wrap" },

  // Resources panel (UPDATE 8) — read-only reference links per evidence
  // category (cqcTaxonomy.js's EVIDENCE_CATEGORIES[].resources), same
  // scrollable-list rhythm as the proof panel above it.
  resourceList: { display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflow: "auto" },
  resourceLink: { display: "flex", alignItems: "flex-start", gap: 7, padding: "7px 8px", borderRadius: 7,
                  border: "1px solid #e2e8f0", background: "#fff", fontSize: 12.5, color: "#2563eb",
                  textDecoration: "none", fontWeight: 600 },

  empty: { fontSize: 13, color: "#9ca3af", padding: "40px 0", textAlign: "center" },
  err: { color: "#991b1b", fontSize: 12.5, padding: "8px 0" },
};

function isoDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
}

// A proof item's own viewer URL — an uploaded file goes through the same
// documents endpoint the old single-source model used; a link item is just
// its URL. Used both for the "View" click and as the popup-window target.
function proofItemUrl(it) {
  if (it.kind === "upload" && it.documentId) return cqcApi.documentUrl(it.documentId);
  if (it.kind === "link" && it.url) return it.url;
  return null;
}

function openProofPopup(it) {
  const url = proofItemUrl(it);
  if (!url) return;
  // A real popup window (not a modal) per the redesign request, sized for
  // a document/webpage preview. If the browser blocks it (popup blocker),
  // window.open returns null/undefined — fall back to a normal new tab so
  // the click still does something.
  const popup = window.open(url, "_blank", "width=900,height=800,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes");
  if (!popup) window.open(url, "_blank", "noreferrer");
}

function ProofAddForm({ row, onAdd }) {
  const [kind, setKind] = useState("link");
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [reviewDate, setReviewDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function reset() {
    setLabel(""); setUrl(""); setFile(null); setReviewDate("");
  }

  function onFileChange(e) {
    const f = e.target.files && e.target.files[0];
    setErr(null);
    if (f && f.size > MAX_UPLOAD_BYTES) {
      setErr(`"${f.name}" is ${fmtBytes(f.size)} — the limit is ${fmtBytes(MAX_UPLOAD_BYTES)}. Link to it instead if it's larger.`);
      setFile(null);
      return;
    }
    setFile(f || null);
  }

  async function submit() {
    setErr(null);
    if (kind === "link" && !url.trim()) { setErr("Enter a link."); return; }
    if (kind === "upload" && !file) { setErr("Choose a file."); return; }
    setBusy(true);
    try {
      const payload = {
        siteId: row.site._id, groupId: row.group.id, itemId: row.item.id, elementId: row.element.id,
        kind, label: label.trim() || null, reviewDate: reviewDate || null,
      };
      if (kind === "link") {
        payload.url = url.trim();
      } else {
        const b64 = await readFileAsBase64(file);
        payload.filename = file.name;
        payload.contentType = file.type || "application/octet-stream";
        payload.fileBase64 = b64;
      }
      await onAdd(payload);
      reset();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={S.addForm}>
      <div style={S.addFormRow}>
        <span style={S.kindToggle(kind === "link")} onClick={() => setKind("link")}>🔗 Link</span>
        <span style={S.kindToggle(kind === "upload")} onClick={() => setKind("upload")}>📎 Upload</span>
        <input style={S.addInput} placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
      </div>
      <div style={S.addFormRow}>
        {kind === "link" ? (
          <input style={S.addInput} placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} />
        ) : (
          <input style={S.addInput} type="file" onChange={onFileChange} />
        )}
        <input style={{ ...S.addInput, flex: "0 0 140px" }} type="date" value={reviewDate}
               onChange={(e) => setReviewDate(e.target.value)} title="Review date (optional)" />
        <button style={S.addBtn} onClick={submit} disabled={busy}>{busy ? "Adding…" : "+ Add proof"}</button>
      </div>
      {kind === "upload" && <div style={S.addHint}>Up to {fmtBytes(MAX_UPLOAD_BYTES)}. Larger files: link to it instead.</div>}
      {err && <div style={S.addErr}>{err}</div>}
    </div>
  );
}

function ProofPanel({ row, evidenceItems, onAdd, onRemove }) {
  return (
    <>
      <div style={S.proofList}>
        {evidenceItems.length === 0 && <div style={S.proofEmpty}>No proof added yet.</div>}
        {evidenceItems.map((it) => {
          const reviewPassed = it.reviewDate && new Date(it.reviewDate).getTime() < Date.now();
          return (
            <div key={it._id} style={S.proofItem}>
              <span style={S.proofIcon}>{it.kind === "upload" ? "📎" : "🔗"}</span>
              <div style={S.proofMain}>
                <button style={S.proofLabelBtn} onClick={() => openProofPopup(it)} title="Opens in a popup window">
                  {it.label || it.filename || it.url || "Untitled"}
                </button>
                <div style={S.proofMeta}>
                  {it.kind === "upload" ? (it.filename || "Uploaded file") : "Link"}
                  {it.kind === "upload" && it.bytes ? ` · ${fmtBytes(it.bytes)}` : ""}
                </div>
                {it.reviewDate && (
                  <div style={reviewPassed ? S.proofReviewOverdue : S.proofReviewOk}>
                    Review {new Date(it.reviewDate).toLocaleDateString()}{reviewPassed ? " — passed" : ""}
                  </div>
                )}
              </div>
              <button style={S.proofRemoveBtn} title="Remove" onClick={() => onRemove(it._id)}>✕</button>
            </div>
          );
        })}
      </div>
      <ProofAddForm row={row} onAdd={onAdd} />
    </>
  );
}

function WorklistRow({
  row, contributorsForSite, onSaveAssignment, onOpenMessages, onOpenComments, onQuickAddContributor,
  expanded, onToggleExpanded, breadcrumbOpen, onToggleBreadcrumb, onAddEvidenceItem, onRemoveEvidenceItem,
}) {
  const { site, group, item, element, assignment, status, evidenceItems } = row;
  const guidance = elementGuidance(group.id, item.id, element.id);
  const proofCount = evidenceItems.length;

  return (
    <>
      <div style={S.row(status)}>
        <div><StatusBadge status={status} /></div>
        <div style={S.siteCell}>{site.name}</div>
        <div>
          <button style={S.labelBtn} onClick={() => onToggleBreadcrumb(row.key)}>
            <span style={S.itemLabel}>{item.label}</span>
            {" — "}
            <span style={S.elLabel}>{element.label}</span>
          </button>
          {breadcrumbOpen && (
            <div style={S.breadcrumb}>{group.label} › {item.label} › {element.label}</div>
          )}
        </div>
        <div>
          <span style={{ ...S.proofBadge, ...(proofCount === 0 ? S.proofBadgeZero : {}) }} title="Proof items attached">
            {proofCount}
          </span>
        </div>
        <div>
          <select style={S.select} value={(assignment && assignment.contributorId) || ""}
                  onChange={(e) => {
                    if (e.target.value === ADD_CONTRIBUTOR_SENTINEL) { onQuickAddContributor(row); return; }
                    onSaveAssignment(row, { contributorId: e.target.value || null });
                  }}>
            <option value="">Unassigned</option>
            {contributorsForSite.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}{c.role ? ` — ${c.role}` : ""}{c.active === false ? " (inactive)" : ""}
              </option>
            ))}
            <option value={ADD_CONTRIBUTOR_SENTINEL}>+ Add contributor…</option>
          </select>
        </div>
        <div>
          <input type="date" style={S.dateInput}
                 value={isoDate(assignment && assignment.estimatedCompletionDate)}
                 onChange={(e) => onSaveAssignment(row, { estimatedCompletionDate: e.target.value || null })} />
        </div>
        <div>
          <button style={S.expandBtn} onClick={() => onToggleExpanded(row.key)}
                  title={expanded ? "Collapse" : "Expand"} aria-label={expanded ? "Collapse" : "Expand"}>
            {expanded ? "▾" : "▸"}
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>
          <div style={S.details}>
            <div style={S.statusRow}>
              <span style={S.statusLabel}>Status</span>
              <select style={S.statusSelect} value={status === "not-applicable" ? "not-started" : status}
                      onChange={(e) => onSaveAssignment(row, { status: e.target.value })}>
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div style={S.cols}>
              <div style={S.col}>
                <div style={S.colLabel}>What it's about</div>
                <div style={S.colBody}>{guidance.purpose}</div>
              </div>
              <div style={S.col}>
                <div style={S.colLabel}>What proof is needed ({guidance.categoryLabel})</div>
                <div style={S.colBody}>
                  {guidance.categoryHelp}
                  <ul style={S.bullets}>
                    {guidance.proofExamples.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
              </div>
              <div style={S.col}>
                <div style={S.colLabel}>The proof itself ({proofCount})</div>
                <ProofPanel
                  row={row}
                  evidenceItems={evidenceItems}
                  onAdd={(payload) => onAddEvidenceItem(row, payload)}
                  onRemove={(id) => onRemoveEvidenceItem(row, id)}
                />
              </div>
            </div>

            {/* Three more panels below the first row (UPDATE 8): Messages
                (unchanged bidirectional thread), Contributor comment (new —
                an append-only timestamped log, newest entry on top, nothing
                ever editable once posted), and Resources (read-only
                reference links for this evidence category). Same bordered-
                column treatment as the row above, for visual parity. */}
            <div style={S.cols}>
              <div style={S.col}>
                <MessageBlock row={row} onOpenMessages={onOpenMessages} />
              </div>
              <div style={S.col}>
                <CommentLog row={row} onOpenComments={onOpenComments} />
              </div>
              <div style={S.col}>
                <div style={S.colLabel}>Resources ({guidance.resources.length})</div>
                <ResourcesPanel resources={guidance.resources} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MessageBlock({ row, onOpenMessages }) {
  const { messages, postMessage, contributor } = onOpenMessages(row);
  const [body, setBody] = useState("");
  const [role, setRole] = useState("update");

  async function send() {
    if (!body.trim()) return;
    await postMessage(body.trim(), role);
    setBody("");
  }

  const mailSubject = encodeURIComponent(`CQC compliance: ${row.item.label} — ${row.element.label} (${row.site.name})`);
  const mailBody = encodeURIComponent(
    `Hi ${contributor ? contributor.name : ""},\n\nA note on "${row.item.label} — ${row.element.label}" at ${row.site.name}:\n\n`
  );

  return (
    <>
      <div style={S.colLabel}>Messages ({messages.length})</div>
      {messages.length > 0 && (
        <div style={S.msgList}>
          {messages.map((m) => (
            <div key={m._id} style={S.msg(m.authorRole)}>
              <div style={S.msgMeta}>
                {m.authorRole === "supervisor" ? "Supervisor" : "Update"} · {m.authorName || "—"} ·{" "}
                {new Date(m.createdAt).toLocaleString()}
              </div>
              {m.body}
            </div>
          ))}
        </div>
      )}
      <div style={S.msgForm}>
        <select style={S.select} value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="update">Post as: Team update</option>
          <option value="supervisor">Post as: Supervisor comment</option>
        </select>
        <input style={S.msgInput} placeholder="Add a message…" value={body}
               onChange={(e) => setBody(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
        <button style={S.sendBtn} onClick={send} disabled={!body.trim()}>Send</button>
      </div>
      {contributor && contributor.email && (
        <div style={{ marginTop: 6 }}>
          <a style={S.mailLink} href={`mailto:${contributor.email}?subject=${mailSubject}&body=${mailBody}`}>
            Email {contributor.name} ({contributor.email}) instead
          </a>
        </div>
      )}
    </>
  );
}

// Contributor comment log (UPDATE 8) — replaces the old single mutable
// "contributor comment" textarea in the Worklist's own UI with an
// append-only, timestamped, immutable log: every post is a new entry, never
// an edit, newest entry at the top of the list ("the next comment going on
// top"). Backed by the same cqc_messages collection as Messages, just
// filtered to kind:'comment' — see cqc_store.js's addMessage. The form sits
// ABOVE the list here (unlike Messages' form-below-list), since that's
// where a freshly-posted, now-top-of-list entry will actually land.
function CommentLog({ row, onOpenComments }) {
  const { comments, postComment } = onOpenComments(row);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function send() {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await postComment(body.trim());
      setBody("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div style={S.colLabel}>Contributor comment ({comments.length})</div>
      <div style={S.commentForm}>
        <textarea style={S.textarea} value={body} onChange={(e) => setBody(e.target.value)}
                  placeholder="e.g. Policy drafted, awaiting sign-off from clinical lead" />
        <button style={S.addBtn} onClick={send} disabled={busy || !body.trim()}>
          {busy ? "Posting…" : "Post comment"}
        </button>
      </div>
      <div style={S.commentList}>
        {comments.length === 0 && <div style={S.proofEmpty}>No comments yet.</div>}
        {comments.map((c) => (
          <div key={c._id} style={S.commentItem}>
            <div style={S.commentMeta}>{c.authorName || "—"} · {new Date(c.createdAt).toLocaleString()}</div>
            <div style={S.commentBody}>{c.body}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// Resources panel (UPDATE 8) — read-only, per evidence category (there's
// nothing per-site or per-organisation to store here; the same 6 categories'
// resource lists apply everywhere, exactly like proofExamples). Opens in a
// new tab rather than a popup window — these are reference material to keep
// reading alongside the row, not a single document to glance at and close.
function ResourcesPanel({ resources }) {
  if (!resources || resources.length === 0) {
    return <div style={S.proofEmpty}>No resources listed for this evidence category yet.</div>;
  }
  return (
    <div style={S.resourceList}>
      {resources.map((r, i) => (
        <a key={i} href={r.url} target="_blank" rel="noreferrer" style={S.resourceLink}>
          <span style={S.proofIcon}>{r.kind === "document" ? "📄" : "🌐"}</span>
          <span>{r.label}</span>
        </a>
      ))}
    </div>
  );
}

export default function CqcWorklist() {
  const [sites, setSites] = useState([]);
  const [applicability, setApplicability] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [evidenceItems, setEvidenceItems] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedSites, setSelectedSites] = useState(null); // null = all
  const [contributorFilter, setContributorFilter] = useState(""); // "" = all, "unassigned" = special
  const [onlyOutstanding, setOnlyOutstanding] = useState(false);
  const [search, setSearch] = useState("");

  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [breadcrumbKeys, setBreadcrumbKeys] = useState(() => new Set());
  const [showContributors, setShowContributors] = useState(false);
  const [quickAddRow, setQuickAddRow] = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const [sitesRes, appRes, assignRes, evidRes, contribRes, msgRes] = await Promise.all([
        cqcApi.listSites(), cqcApi.listApplicability(),
        cqcApi.listAssignments(), cqcApi.listEvidenceItems(), cqcApi.listContributors(), cqcApi.listMessages(),
      ]);
      setSites(sitesRes.sites || []);
      setApplicability(appRes.applicability || []);
      setAssignments(assignRes.assignments || []);
      setEvidenceItems(evidRes.evidenceItems || []);
      setContributors(contribRes.contributors || []);
      setMessages(msgRes.messages || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const appByKey = useMemo(() => applicabilityIndex(applicability), [applicability]);
  const assignByKey = useMemo(() => assignmentIndex(assignments), [assignments]);
  const evidenceByKey = useMemo(() => evidenceItemIndex(evidenceItems), [evidenceItems]);
  const contributorById = useMemo(() => {
    const m = {};
    for (const c of contributors) m[c._id] = c;
    return m;
  }, [contributors]);

  const rows = useMemo(() => {
    const out = [];
    for (const site of sites) {
      for (const group of GROUPS) {
        for (const item of group.items) {
          if (!itemApplicableForSite(appByKey, site._id, group.id, item.id)) continue;
          for (const element of item.elements) {
            const key = `${site._id}|${group.id}|${item.id}|${element.id}`;
            const assignment = assignByKey[key];
            out.push({
              key, site, group, item, element, assignment,
              status: elementStatus({ applicable: true, assignment }),
              evidenceItems: evidenceByKey[key] || [],
            });
          }
        }
      }
    }
    return out;
  }, [sites, appByKey, assignByKey, evidenceByKey]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (selectedSites && !selectedSites.has(r.site._id)) return false;
      if (contributorFilter === "unassigned" && r.assignment && r.assignment.contributorId) return false;
      if (contributorFilter && contributorFilter !== "unassigned" && (!r.assignment || r.assignment.contributorId !== contributorFilter)) return false;
      if (onlyOutstanding && !(r.status === "not-started" || r.status === "ongoing")) return false;
      if (q) {
        const hay = `${r.site.name} ${r.group.label} ${r.item.label} ${r.element.label}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, selectedSites, contributorFilter, onlyOutstanding, search]);

  const counts = useMemo(() => {
    const c = { "not-started": 0, "ongoing": 0, "completed": 0 };
    for (const r of rows) if (c[r.status] != null) c[r.status]++;
    return c;
  }, [rows]);

  function toggleSite(siteId) {
    setSelectedSites((prev) => {
      const all = new Set(sites.map((s) => s._id));
      const cur = prev || all;
      const next = new Set(cur);
      if (next.has(siteId)) next.delete(siteId); else next.add(siteId);
      // If everything ends up selected, treat as "all" (null) for simplicity.
      return next.size === all.size ? null : next;
    });
  }

  async function saveAssignment(row, patch) {
    const { site, group, item, element } = row;
    await cqcApi.saveAssignment({ siteId: site._id, groupId: group.id, itemId: item.id, elementId: element.id, ...patch });
    const res = await cqcApi.listAssignments();
    setAssignments(res.assignments || []);
  }

  async function addEvidenceItemFor(row, payload) {
    await cqcApi.addEvidenceItem(payload);
    const res = await cqcApi.listEvidenceItems();
    setEvidenceItems(res.evidenceItems || []);
  }

  async function removeEvidenceItemFor(row, evidenceItemId) {
    await cqcApi.removeEvidenceItem(evidenceItemId);
    const res = await cqcApi.listEvidenceItems();
    setEvidenceItems(res.evidenceItems || []);
  }

  // A contributor added on the fly from a row's "+ Add contributor…" option
  // (CqcQuickAddContributor.js): refresh the contributors list so they show
  // up everywhere else too, then assign them to the row that opened the
  // popup, exactly as if they'd been picked from the dropdown.
  async function contributorAdded(row, contributor) {
    const res = await cqcApi.listContributors();
    setContributors(res.contributors || []);
    setQuickAddRow(null);
    await saveAssignment(row, { contributorId: contributor._id });
  }

  // Messages and the Contributor comment log (UPDATE 8) both live in
  // cqc_messages now, distinguished only by `kind` — threadFor/postToThread
  // are the shared plumbing; openMessagesFor/openCommentsFor below adapt
  // that into what MessageBlock/CommentLog each expect.
  function threadFor(row, kind) {
    return messages.filter((m) =>
      m.siteId === row.site._id && m.groupId === row.group.id && m.itemId === row.item.id && m.elementId === row.element.id &&
      ((m.kind || "message") === kind)
    );
  }

  async function postToThread(row, kind, body, authorRole) {
    await cqcApi.addMessage({ siteId: row.site._id, groupId: row.group.id, itemId: row.item.id, elementId: row.element.id, body, authorRole, kind });
    const res = await cqcApi.listMessages();
    setMessages(res.messages || []);
  }

  function openMessagesFor(row) {
    const contributor = row.assignment && row.assignment.contributorId ? contributorById[row.assignment.contributorId] : null;
    return {
      messages: threadFor(row, "message"),
      contributor,
      postMessage: (body, authorRole) => postToThread(row, "message", body, authorRole),
    };
  }

  function openCommentsFor(row) {
    // Newest-first per the request ("the next comment going on top") — the
    // store itself always returns oldest-first (listMessages sorts
    // createdAt:1, same as Messages); only the display order is reversed
    // here, so nothing about how comments are stored/audited changes.
    return {
      comments: threadFor(row, "comment").slice().reverse(),
      postComment: (body) => postToThread(row, "comment", body),
    };
  }

  function toggleExpanded(key) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }
  function toggleBreadcrumb(key) {
    setBreadcrumbKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  if (loading) return <div style={S.wrap}>Loading worklist…</div>;
  if (error) return <div style={{ ...S.wrap, color: "#991b1b" }}>Couldn't load worklist: {error}</div>;
  if (sites.length === 0) {
    return <div style={S.wrap}><div style={S.empty}>No sites connected yet — import your CQC-registered sites first.</div></div>;
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>CQC Worklist</h1>
          <div style={S.sub}>Every outstanding evidence item, across every site, in one flat list.</div>
        </div>
        <button style={S.manageBtn} onClick={() => setShowContributors(true)}>Manage contributors</button>
      </div>

      <div style={S.filters}>
        {sites.map((s) => {
          const active = !selectedSites || selectedSites.has(s._id);
          return <div key={s._id} style={S.chip(active)} onClick={() => toggleSite(s._id)}>{s.name}</div>;
        })}
        <select style={S.select} value={contributorFilter} onChange={(e) => setContributorFilter(e.target.value)}>
          <option value="">All contributors</option>
          <option value="unassigned">Unassigned</option>
          {contributors.filter((c) => c.active !== false).map((c) => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>
        <label style={S.checkLbl}>
          <input type="checkbox" checked={onlyOutstanding} onChange={(e) => setOnlyOutstanding(e.target.checked)} />
          Only not started &amp; ongoing
        </label>
        <input style={S.search} placeholder="Search site / statement…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <span style={S.counts}>
          {counts["not-started"]} not started · {counts["ongoing"]} ongoing · {counts["completed"]} completed
        </span>
      </div>

      <div style={S.listWrap}>
        <div style={S.colHead}>
          <div>Status</div><div>Site</div><div>Statement</div><div>Proof</div><div>Assigned to</div><div>Est. completion</div><div></div>
        </div>
        {filteredRows.length === 0 && <div style={S.empty}>No items match these filters.</div>}
        {filteredRows.map((row) => (
          <WorklistRow
            key={row.key}
            row={row}
            contributorsForSite={contributors.filter((c) => c.siteId === row.site._id)}
            onSaveAssignment={saveAssignment}
            onOpenMessages={openMessagesFor}
            onOpenComments={openCommentsFor}
            onQuickAddContributor={setQuickAddRow}
            onAddEvidenceItem={addEvidenceItemFor}
            onRemoveEvidenceItem={removeEvidenceItemFor}
            expanded={expandedKeys.has(row.key)}
            onToggleExpanded={toggleExpanded}
            breadcrumbOpen={breadcrumbKeys.has(row.key)}
            onToggleBreadcrumb={toggleBreadcrumb}
          />
        ))}
      </div>

      {showContributors && (
        <CqcContributors
          sites={sites}
          onClose={() => setShowContributors(false)}
          onChanged={async () => { const res = await cqcApi.listContributors(); setContributors(res.contributors || []); }}
        />
      )}

      {quickAddRow && (
        <CqcQuickAddContributor
          site={quickAddRow.site}
          onClose={() => setQuickAddRow(null)}
          onCreated={(contributor) => contributorAdded(quickAddRow, contributor)}
        />
      )}
    </div>
  );
}
